import { ApiError, pushApi } from '../api/client';
import { isIos, isStandalone } from './pwa';

/**
 * Notifications on this device: whether it can have them, turning them on (asks permission, then
 * signs this browser up with Stitch's push key) and off, and signing up again when Stitch opens
 * (after signing in again, or when the server's key changed).
 */

export type PushSupport =
  /** Can be turned on here. */
  | 'ok'
  /** The development server has no service worker. */
  | 'dev'
  /** Not https. */
  | 'insecure'
  /** iPhone or iPad in the browser: notifications work in the installed app only. */
  | 'ios-install'
  /** The installed app on iOS before 16.4. */
  | 'ios-old'
  /** This browser can't. */
  | 'unsupported';

export function pushSupport(): PushSupport {
  if (!import.meta.env.PROD) return 'dev';
  if (!window.isSecureContext) return 'insecure';
  const ios = isIos();
  if (ios && !isStandalone()) return 'ios-install';
  if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) return ios ? 'ios-old' : 'unsupported';
  return 'ok';
}

/** Whether this browser lets Stitch show notifications: asked yet, allowed, or blocked. */
export function permission(): NotificationPermission | 'unsupported' {
  return 'Notification' in window ? Notification.permission : 'unsupported';
}

/** Who turned notifications on in this browser (so someone else signing in here isn't signed up for them). */
const onKey = (userId: string) => `stitch.push.on.${userId}`;
function remember(userId: string, on: boolean): void {
  try {
    if (on) localStorage.setItem(onKey(userId), '1');
    else localStorage.removeItem(onKey(userId));
  } catch {
    /* private mode */
  }
}
function remembered(userId: string): boolean {
  try {
    return localStorage.getItem(onKey(userId)) === '1';
  } catch {
    return false;
  }
}

async function registration(): Promise<ServiceWorkerRegistration> {
  const ready = navigator.serviceWorker.ready;
  const late = new Promise<never>((_, reject) => window.setTimeout(() => reject(new Error('Stitch’s app support isn’t ready yet. Reload the page and try again.')), 15_000));
  return Promise.race([ready, late]);
}

function keyBytes(base64url: string): Uint8Array<ArrayBuffer> {
  const b64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64 + '='.repeat((4 - (b64.length % 4)) % 4));
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

function sameKey(sub: PushSubscription, key: Uint8Array): boolean {
  const had = sub.options?.applicationServerKey;
  if (!had) return true;
  const a = new Uint8Array(had);
  return a.length === key.length && a.every((b, i) => b === key[i]);
}

/** This browser's subscription with Stitch's current key, made if needed, and told to Stitch. */
async function signUp(): Promise<void> {
  const reg = await registration();
  const key = keyBytes((await pushApi.key()).publicKey);
  let sub = await reg.pushManager.getSubscription();
  if (sub && !sameKey(sub, key)) {
    await sub.unsubscribe().catch(() => undefined);
    sub = null;
  }
  sub ??= await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key });
  await pushApi.subscribe(sub.toJSON());
}

/** Whether notifications are on for this device for this person, as far as this browser knows. */
export async function onHere(userId: string): Promise<boolean> {
  if (pushSupport() !== 'ok' || permission() !== 'granted' || !remembered(userId)) return false;
  try {
    const reg = await registration();
    return Boolean(await reg.pushManager.getSubscription());
  } catch {
    return false;
  }
}

/** Asks the browser for permission, then signs this device up. */
export async function turnOnHere(userId: string): Promise<'on' | 'denied' | 'dismissed'> {
  const answer = await Notification.requestPermission();
  if (answer === 'denied') return 'denied';
  if (answer !== 'granted') return 'dismissed';
  await signUp();
  remember(userId, true);
  return 'on';
}

export async function turnOffHere(userId: string): Promise<void> {
  remember(userId, false);
  const reg = await registration();
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return;
  try {
    await pushApi.unsubscribe({ endpoint: sub.endpoint });
  } catch (err) {
    // Already gone from Stitch's side: fine.
    if (!(err instanceof ApiError && err.status === 404)) throw err;
  }
  await sub.unsubscribe().catch(() => undefined);
}

let synced: string | null = null;
/**
 * When Stitch opens: if this person turned notifications on here, tell Stitch about this device
 * again. It stops getting them when a sign-in ends, and signing in again starts them here.
 */
export async function syncHere(userId: string): Promise<void> {
  if (synced === userId) return;
  synced = userId;
  if (pushSupport() !== 'ok' || permission() !== 'granted' || !remembered(userId)) return;
  await signUp();
}
